import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Row, Col, ListGroup, Image, Card, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Message from "../components/Message";
import CheckoutSteps from "../components/CheckoutSteps";
import Loader from "../components/Loader";
import { useCreateOrderMutation } from "../slices/ordersApiSlice";
import { clearCartItems } from "../slices/cartSlice";
import { calcShippingPrice } from "../utils/cartUtils"

const PlaceOrderScreen = () => {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);

  const cart = useSelector((state) => state.cart);

  const [createOrder, { isLoading, error }] = useCreateOrderMutation();

  useEffect(() => {
    if (!cart.shippingAddress.address) {
      navigate("/shipping");
    } else if (!cart.paymentMethod) {
      navigate("/payment");
    }
  }, [cart.paymentMethod, cart.shippingAddress.address, navigate]);

  const dispatch = useDispatch();

  // Recalculate shipping price
  const shippingPrice = calcShippingPrice(cart.shippingAddress);
  
  // Recalculate total price to match
  const totalPrice = Math.round(
    Number(cart.itemsPrice) + 
    Number(shippingPrice) + 
    0 // No tax
  );


  const placeOrderHandler = async () => {
    if (!agreed) {
      toast.error("יש לאשר את תקנון האתר");
      return;
    }
    try {
      const res = await createOrder({
        orderItems: cart.cartItems,
        shippingAddress: cart.shippingAddress,
        paymentMethod: cart.paymentMethod,
        itemsPrice: cart.itemsPrice,
        shippingPrice: shippingPrice, // Use recalculated shipping
        taxPrice: "0", // No tax
        totalPrice: totalPrice, // Use recalculated total
      }).unwrap();
      dispatch(clearCartItems());
      navigate(`/order/${res._id}`);
    } catch (err) {
      toast.error(err);
    }
  };

  
  return (
    <>
      <CheckoutSteps step1 step2 step3 step4 />
      <Row>
        <Col md={4}>
          <Card>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2 className="text-end">סיכום הזמנה</h2>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{cart.itemsPrice}</Col>
                  <Col className="text-end">פריטים</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{shippingPrice}</Col>
                  <Col className="text-end">משלוח</Col>
                </Row>
              </ListGroup.Item>
              {/* <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪0</Col>
                  <Col className="text-end">מס</Col>
                </Row>
              </ListGroup.Item> */}
              <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{totalPrice}</Col>
                  <Col className="text-end">סה"כ</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Form.Group controlId="agreement">
                  <Col xs={12}>
                    <div className="d-flex justify-content-end align-items-center">
                      <label 
                        htmlFor="agreement" 
                        className="me-2" 
                        style={{ fontSize: "0.9rem", cursor: "pointer" }}
                      >
                        אני מאשר/ת כי ביצוע הרכישה כפוף לתקנון האתר
                      </label>
                      <input
                        type="checkbox"
                        id="agreement"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer"
                        }}
                      />
                    </div>
                  </Col>
                </Form.Group>
              </ListGroup.Item>
              <ListGroup.Item>
                {error && (
                  <Message variant="danger">{error.data.message}</Message>
                )}
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col className="d-flex justify-content-end">
                    <Button
                      type="button"
                      className="btn-block"
                      disabled={cart.cartItems === 0}
                      onClick={placeOrderHandler}
                    >
                      בצע הזמנה
                    </Button>
                  </Col>
                </Row>
                {isLoading && <Loader />}
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2 className="text-end">משלוח</h2>
              <p className="text-end">
                <strong>כתובת:</strong>
                {cart.shippingAddress.address}, {cart.shippingAddress.city}{" "}
                {cart.shippingAddress.postalCode} {cart.shippingAddress.country}
              </p>
            </ListGroup.Item>

            <ListGroup.Item className="text-end">
              <h2 className="text-end">שיטת תשלום</h2>
              {cart.paymentMethod}
              <strong> :שיטה</strong>
            </ListGroup.Item>

            <ListGroup.Item>
              <h2 className="text-end">מוצרים להזמנה</h2>
              {cart.cartItems.length === 0 ? (
                <Message>העגלה שלך ריקה</Message>
              ) : (
                <ListGroup variant="flush">
                  {cart.cartItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row>
                        <Col md={4}>
                          {item.qty} x ₪{item.price} = ₪
                          {(item.qty * (item.price * 100)) / 100}
                        </Col>
                        <Col>
                          <Link to={`/product/${item.product}`}>
                            {item.name}
                          </Link>
                          {item.customComment && (
                            <div className="text-end mt-1" style={{ fontSize: '0.85rem', color: '#555' }}>
                              <strong>הערה: </strong>{item.customComment}
                            </div>
                          )}
                        </Col>
                        <Col md={1}>
                          <Image
                            src={item.image}
                            alt={item.name}
                            fluid
                            rounded
                          />
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>
      </Row>
    </>
  );
};

export default PlaceOrderScreen;
